import { TaskVisibilityValues } from "@/types";

import { UNTITLED_CAREER_PLAN_TITLE } from "./_CareerPlanTitle";

export const CAREER_PLAN_STATUS = {
  DRAFT: "DRAFT",
  PUBLISHED: "PUBLISHED",
  UNPUBLISHED: "UNPUBLISHED",
  COMPLETED: "COMPLETED"
} as const;

export type CareerPlanStatus = (typeof CAREER_PLAN_STATUS)[keyof typeof CAREER_PLAN_STATUS];

export const CAREER_PLAN_HIGHLIGHT_TYPE = {
  STRENGTH: "STRENGTH",
  FOCUS: "FOCUS"
} as const;

export type CareerPlanHighlightType = (typeof CAREER_PLAN_HIGHLIGHT_TYPE)[keyof typeof CAREER_PLAN_HIGHLIGHT_TYPE];

export type CareerPlanGraphqlHighlight = {
  id: string;
  highlightType: CareerPlanHighlightType;
  title: string | null;
  description: string | null;
};

export type CareerPlanHighlightFields = {
  id?: string;
  title: string;
  description: string;
};

export type CareerPlanStrengthFields = CareerPlanHighlightFields;
export type CareerPlanFocusAreaFields = CareerPlanHighlightFields;

export type CareerPlanActionItem = {
  id?: string;
  focusArea: string;
  recommendedActivity: string;
  successMeasure: string;
  timeframe: string;
};

export type CareerPlanHighlightInput = {
  id?: string;
  highlightType: CareerPlanHighlightType;
  title?: string | null;
  description?: string | null;
};

export type CareerPlanActionItemInput = {
  id?: string;
  focusArea?: string | null;
  recommendedActivity?: string | null;
  successMeasure?: string | null;
  timeframe?: string | null;
};

export type CareerPlanFollowUpTaskInput = {
  name: string;
  description: string;
  assignedToId?: string | null;
  dueDate?: string | null;
  visibility?: string;
};

export type CreateCareerPlanInput = {
  pgaId: string;
  title?: string | null;
  timeframe?: string | null;
  careerTarget?: string | null;
  additionalNotes?: string | null;
  highlights: CareerPlanHighlightInput[];
  actionItems: CareerPlanActionItemInput[];
  task?: CareerPlanFollowUpTaskInput;
};

export type UpdateCareerPlanInput = {
  id: string;
  title?: string | null;
  timeframe?: string | null;
  careerTarget?: string | null;
  additionalNotes?: string | null;
  highlights: CareerPlanHighlightInput[];
  actionItems: CareerPlanActionItemInput[];
  task?: CareerPlanFollowUpTaskInput;
};

export type CareerPlanDocumentFormValues = {
  title: string;
  timeframe: string;
  careerTarget: string;
  strengths: CareerPlanStrengthFields[];
  focusAreas: CareerPlanFocusAreaFields[];
  actionItems: CareerPlanActionItem[];
  additionalNotes: string;
  status: CareerPlanStatus;
};

export const MAX_CAREER_PLAN_CARDS = 3;

export type CareerPlanSection =
  | "details"
  | "careerTarget"
  | "strengths"
  | "focusAreas"
  | "actionPlan"
  | "additionalNotes";

export const CAREER_PLAN_INTRO =
  "A Career Plan is a one-page document you write for one career target. Use it to capture the target, strengths they bring, areas of focus, and action plan from your conversation. Keep it as a draft while you work, then publish when it is ready for the member.";

export const EMPTY_HIGHLIGHT: CareerPlanHighlightFields = {
  title: "",
  description: ""
};

export const EMPTY_STRENGTH = EMPTY_HIGHLIGHT;
export const EMPTY_FOCUS_AREA = EMPTY_HIGHLIGHT;

export const EMPTY_ACTION_ITEM: CareerPlanActionItem = {
  focusArea: "",
  recommendedActivity: "",
  successMeasure: "",
  timeframe: ""
};

export function careerPlanFollowUpDueDate(from = new Date(), days = 7): { startDate: Date; endDate: Date } {
  const date = new Date(from);
  date.setDate(date.getDate() + days);
  return { startDate: date, endDate: date };
}

export function careerPlanFollowUpTaskName(title: string, memberName: string): string {
  const planTitle = title.trim() || UNTITLED_CAREER_PLAN_TITLE;
  const member = memberName.trim();
  return member ? `Follow up: ${planTitle} — ${member}` : `Follow up: ${planTitle}`;
}

export function careerPlanFollowUpTaskDescription(memberName: string): string {
  const member = memberName.trim() || "this member";
  return `Follow up on this Career Plan with ${member}.`;
}

export function buildCareerPlanFollowUpTaskInput({
  title,
  memberName,
  assignedToId,
  dueDate
}: {
  title: string;
  memberName: string;
  assignedToId?: string | null;
  dueDate?: string | null;
}): CareerPlanFollowUpTaskInput {
  return {
    name: careerPlanFollowUpTaskName(title, memberName),
    description: careerPlanFollowUpTaskDescription(memberName),
    ...(assignedToId ? { assignedToId } : {}),
    ...(dueDate ? { dueDate } : {}),
    visibility: TaskVisibilityValues.PRIVATE
  };
}

export function emptyCareerPlanFormValues(): CareerPlanDocumentFormValues {
  return {
    title: "",
    timeframe: "",
    careerTarget: "",
    strengths: [],
    focusAreas: [],
    actionItems: [],
    additionalNotes: "",
    status: CAREER_PLAN_STATUS.DRAFT
  };
}

export function normalizeCareerPlanStatus(status?: string | null): CareerPlanStatus {
  if (status === CAREER_PLAN_STATUS.PUBLISHED) return status;
  if (status === CAREER_PLAN_STATUS.UNPUBLISHED) return status;
  if (status === CAREER_PLAN_STATUS.COMPLETED) return status;
  return CAREER_PLAN_STATUS.DRAFT;
}

export function formValuesFromCareerPlan(plan: {
  title?: string | null;
  timeframe?: string | null;
  careerTarget?: string | null;
  highlights?: Array<{
    id?: string;
    highlightType?: string | null;
    title?: string | null;
    description?: string | null;
  }> | null;
  actionItems?: Array<{
    id?: string;
    focusArea?: string | null;
    recommendedActivity?: string | null;
    successMeasure?: string | null;
    timeframe?: string | null;
  }> | null;
  additionalNotes?: string | null;
  status?: string | null;
}): CareerPlanDocumentFormValues {
  return {
    title: plan.title ?? "",
    timeframe: plan.timeframe ?? "",
    careerTarget: plan.careerTarget ?? "",
    strengths: (plan.highlights ?? [])
      .filter((item) => item.highlightType === CAREER_PLAN_HIGHLIGHT_TYPE.STRENGTH)
      .map(highlightFromPlan),
    focusAreas: (plan.highlights ?? [])
      .filter((item) => item.highlightType === CAREER_PLAN_HIGHLIGHT_TYPE.FOCUS)
      .map(highlightFromPlan),
    actionItems: (plan.actionItems ?? []).map(actionItemFromPlan),
    additionalNotes: plan.additionalNotes ?? "",
    status: normalizeCareerPlanStatus(plan.status)
  };
}

export function isPublishedCareerPlan(status: CareerPlanStatus): boolean {
  return status === CAREER_PLAN_STATUS.PUBLISHED;
}

export function isUnpublishedCareerPlan(status: CareerPlanStatus): boolean {
  return status === CAREER_PLAN_STATUS.UNPUBLISHED;
}

export function isCompletedCareerPlan(status: CareerPlanStatus): boolean {
  return status === CAREER_PLAN_STATUS.COMPLETED;
}

export function isBlankCareerPlanHighlight(item: CareerPlanHighlightFields): boolean {
  return item.title.trim() === "" && item.description.trim() === "";
}

export function isBlankCareerPlanActionItem(item: CareerPlanActionItem): boolean {
  return (
    item.focusArea.trim() === "" &&
    item.recommendedActivity.trim() === "" &&
    item.successMeasure.trim() === "" &&
    item.timeframe.trim() === ""
  );
}

export function pruneEmptyCareerPlanCards(
  section: "strengths" | "focusAreas" | "actionPlan",
  values: CareerPlanDocumentFormValues
): Partial<CareerPlanDocumentFormValues> {
  if (section === "strengths") {
    return { strengths: values.strengths.filter((item) => !isBlankCareerPlanHighlight(item)) };
  }

  if (section === "focusAreas") {
    return { focusAreas: values.focusAreas.filter((item) => !isBlankCareerPlanHighlight(item)) };
  }

  return { actionItems: values.actionItems.filter((item) => !isBlankCareerPlanActionItem(item)) };
}

export function pruneCareerPlanFormValues(values: CareerPlanDocumentFormValues): CareerPlanDocumentFormValues {
  return {
    ...values,
    ...pruneEmptyCareerPlanCards("strengths", values),
    ...pruneEmptyCareerPlanCards("focusAreas", values),
    ...pruneEmptyCareerPlanCards("actionPlan", values)
  };
}

export function areCareerPlanFormValuesEqual(
  left: CareerPlanDocumentFormValues,
  right: CareerPlanDocumentFormValues
): boolean {
  return JSON.stringify(comparableCareerPlanFormValues(left)) === JSON.stringify(comparableCareerPlanFormValues(right));
}

function comparableCareerPlanFormValues(values: CareerPlanDocumentFormValues) {
  return {
    title: values.title,
    timeframe: values.timeframe,
    careerTarget: values.careerTarget,
    additionalNotes: values.additionalNotes,
    status: values.status,
    strengths: values.strengths.map((item) => ({
      id: item.id ?? null,
      title: item.title,
      description: item.description
    })),
    focusAreas: values.focusAreas.map((item) => ({
      id: item.id ?? null,
      title: item.title,
      description: item.description
    })),
    actionItems: values.actionItems.map((item) => ({
      id: item.id ?? null,
      focusArea: item.focusArea,
      recommendedActivity: item.recommendedActivity,
      successMeasure: item.successMeasure,
      timeframe: item.timeframe
    }))
  };
}

function highlightFromPlan(item: {
  id?: string;
  title?: string | null;
  description?: string | null;
}): CareerPlanHighlightFields {
  return {
    ...(item.id ? { id: item.id } : {}),
    title: item.title ?? "",
    description: item.description ?? ""
  };
}

function actionItemFromPlan(item: {
  id?: string;
  focusArea?: string | null;
  recommendedActivity?: string | null;
  successMeasure?: string | null;
  timeframe?: string | null;
}): CareerPlanActionItem {
  return {
    ...(item.id ? { id: item.id } : {}),
    focusArea: item.focusArea ?? "",
    recommendedActivity: item.recommendedActivity ?? "",
    successMeasure: item.successMeasure ?? "",
    timeframe: item.timeframe ?? ""
  };
}

function nullableText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function highlightInput(
  item: CareerPlanHighlightFields,
  highlightType: CareerPlanHighlightType,
  includeId: boolean
): CareerPlanHighlightInput {
  return {
    ...(includeId && item.id ? { id: item.id } : {}),
    highlightType,
    title: nullableText(item.title),
    description: nullableText(item.description)
  };
}

function actionItemInput(item: CareerPlanActionItem, includeId: boolean): CareerPlanActionItemInput {
  return {
    ...(includeId && item.id ? { id: item.id } : {}),
    focusArea: nullableText(item.focusArea),
    recommendedActivity: nullableText(item.recommendedActivity),
    successMeasure: nullableText(item.successMeasure),
    timeframe: nullableText(item.timeframe)
  };
}

function careerPlanWriteFields(values: CareerPlanDocumentFormValues, includeIds: boolean) {
  const pruned = pruneCareerPlanFormValues(values);
  return {
    title: nullableText(pruned.title),
    timeframe: nullableText(pruned.timeframe),
    careerTarget: nullableText(pruned.careerTarget),
    additionalNotes: nullableText(pruned.additionalNotes),
    highlights: [
      ...pruned.strengths.map((item) => highlightInput(item, CAREER_PLAN_HIGHLIGHT_TYPE.STRENGTH, includeIds)),
      ...pruned.focusAreas.map((item) => highlightInput(item, CAREER_PLAN_HIGHLIGHT_TYPE.FOCUS, includeIds))
    ],
    actionItems: pruned.actionItems.map((item) => actionItemInput(item, includeIds))
  };
}

export function buildCreateCareerPlanInput(
  pgaId: string,
  values: CareerPlanDocumentFormValues,
  task?: CareerPlanFollowUpTaskInput | null
): CreateCareerPlanInput {
  return {
    pgaId,
    ...careerPlanWriteFields(values, false),
    ...(task ? { task } : {})
  };
}

export function buildUpdateCareerPlanInput(
  id: string,
  values: CareerPlanDocumentFormValues,
  task?: CareerPlanFollowUpTaskInput | null
): UpdateCareerPlanInput {
  return {
    id,
    ...careerPlanWriteFields(values, true),
    ...(task ? { task } : {})
  };
}

export function moveIndexForReorder(currentIds: string[], nextIds: string[]): { from: number; to: number } | null {
  if (currentIds.length !== nextIds.length || currentIds.length < 2) return null;
  if (currentIds.every((id, index) => id === nextIds[index])) return null;

  for (let from = 0; from < currentIds.length; from++) {
    const to = nextIds.indexOf(currentIds[from]);
    if (to === -1 || to === from) continue;
    const moved = [...currentIds];
    const [item] = moved.splice(from, 1);
    if (item == null) continue;
    moved.splice(to, 0, item);
    if (moved.every((id, index) => id === nextIds[index])) {
      return { from, to };
    }
  }

  return null;
}

export function snapshotCareerPlanSection(
  section: CareerPlanSection,
  values: CareerPlanDocumentFormValues
): Partial<CareerPlanDocumentFormValues> {
  switch (section) {
    case "details":
      return { timeframe: values.timeframe };
    case "careerTarget":
      return { careerTarget: values.careerTarget };
    case "strengths":
      return { strengths: values.strengths.map((item) => ({ ...item })) };
    case "focusAreas":
      return { focusAreas: values.focusAreas.map((item) => ({ ...item })) };
    case "actionPlan":
      return { actionItems: values.actionItems.map((item) => ({ ...item })) };
    case "additionalNotes":
      return { additionalNotes: values.additionalNotes };
  }
}

type EmploymentForPosition = {
  isPrimary?: boolean | null;
  endedOn?: string | null;
  formattedTitle?: string | null;
  title?: string | null;
  jobClassification?: string | null;
};

export function currentPositionLabel(employments: EmploymentForPosition[] = []): string {
  const primary = employments.find((employment) => {
    if (!employment.isPrimary) return false;
    if (!employment.endedOn) return true;
    const endedDate = Date.parse(employment.endedOn);
    return Number.isNaN(endedDate) || endedDate > Date.now();
  });
  if (!primary) return "";
  const title = primary.formattedTitle || primary.title || "";
  return primary.jobClassification ? `${title} (${primary.jobClassification})` : title;
}
